import React, { useState } from 'react';
import { Mail, Phone, MapPin, Clock, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }
    // Mock submission
    toast.success('Message sent! We\'ll get back to you soon.');
    setFormData({ name: '', email: '', phone: '', message: '' });
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-zinc-950 pt-20">
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold mb-4 text-zinc-100">Get In Touch</h1>
          <p className="text-xl text-zinc-400">Have questions? We'd love to hear from you</p>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Contact Form */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name" className="text-zinc-300 mb-2 block">
                    Name *
                  </Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    className="bg-zinc-950 border-zinc-800 text-zinc-100"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-zinc-300 mb-2 block">
                    Email *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className="bg-zinc-950 border-zinc-800 text-zinc-100"
                    placeholder="your.email@example.com"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-zinc-300 mb-2 block">
                    Phone
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    className="bg-zinc-950 border-zinc-800 text-zinc-100"
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div>
                  <Label htmlFor="message" className="text-zinc-300 mb-2 block">
                    Message *
                  </Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => handleChange('message', e.target.value)}
                    className="bg-zinc-950 border-zinc-800 text-zinc-100 min-h-32"
                    placeholder="How can we help you?"
                  />
                </div>
                <Button 
                  type="submit"
                  className="w-full bg-zinc-100 text-zinc-950 hover:bg-zinc-200"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send Message
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl font-bold mb-8 text-zinc-100">Contact Information</h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-zinc-900 rounded-lg flex items-center justify-center flex-shrink-0 border border-zinc-800">
                    <Phone className="w-5 h-5 text-zinc-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-zinc-100 mb-1">Phone and WhatsApp</h3>
                    <p className="text-zinc-400">+351 96 98 52 004</p>
                    <p className="text-sm text-zinc-500 mt-1">Mon-Fri: 8AM - 6PM</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-zinc-900 rounded-lg flex items-center justify-center flex-shrink-0 border border-zinc-800">
                    <MapPin className="w-5 h-5 text-zinc-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-zinc-100 mb-1">Service Area</h3>
                    <p className="text-zinc-400">Mobile Service - Algarve and surrounding</p>
                    <p className="text-sm text-zinc-500 mt-1">Quinta do Lago, Vale do Lobo, Vilamoura</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-zinc-900 rounded-lg flex items-center justify-center flex-shrink-0 border border-zinc-800">
                    <Clock className="w-5 h-5 text-zinc-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-zinc-100 mb-1">Hours</h3>
                    <div className="text-zinc-400 space-y-1">
                      <p>Monday - Friday: 8:00 AM - 6:00 PM</p>
                      <p>Saturday - Sunday: Closed</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* FAQ Section */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="pt-6">
                <h3 className="text-xl font-bold text-zinc-100 mb-4">Frequently Asked Questions</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-zinc-300 mb-2">Do you provide water and power?</h4>
                    <p className="text-sm text-zinc-400">Yes! We come fully equipped with our own water and power supply.</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-zinc-300 mb-2">How long does a detail take?</h4>
                    <p className="text-sm text-zinc-400">Depending on the package, between 2-8 hours. We'll give you an accurate estimate when booking.</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-zinc-300 mb-2">What if I need to reschedule?</h4>
                    <p className="text-sm text-zinc-400">Contact us at least 24 hours in advance for free rescheduling.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
